import { ActionIcon, Box, Button, Text } from '@mantine/core';
import {
  IconPlayerPlayFilled,
  IconPlayerPauseFilled,
  IconVolumeOff,
  IconVolume,
  IconX,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatSecondsToTime } from './timeUtils';

type Props = {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isMuted: boolean;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onMuteToggle: () => void;
  fileName?: string;
  onClose?: () => void;
  // When inline, the player sits within its container (sticky to the bottom of the
  // shell) rather than spanning the full viewport width as a fixed bar.
  inline?: boolean;
};

export const AudioPlayer = ({
  isPlaying,
  currentTime,
  duration,
  isMuted,
  onPlayPause,
  onSeek,
  onMuteToggle,
  fileName,
  onClose,
  inline = false,
}: Props) => {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    onSeek(percent * duration);
  };

  return (
    <Box
      style={{
        ...(inline
          ? { position: 'sticky', bottom: '16px', width: '100%' }
          : { position: 'fixed', bottom: '32px', left: '32px', right: '32px' }),
        backgroundColor: 'white',
        borderRadius: '50px',
        padding: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        zIndex: 1000,
      }}
    >
      <Button
        variant="subtle"
        size="sm"
        style={{
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          padding: 0,
          backgroundColor: 'var(--mantine-color-gray-2)',
        }}
        onClick={onPlayPause}
      >
        {isPlaying ? (
          <IconPlayerPauseFilled size={18} color="black" />
        ) : (
          <IconPlayerPlayFilled size={18} color="black" />
        )}
      </Button>

      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSecondsToTime(currentTime)}
      </Text>

      <Box
        style={{
          flex: 1,
          height: '5px',
          backgroundColor: 'var(--mantine-color-gray-2)',
          borderRadius: '3px',
          cursor: 'pointer',
          position: 'relative',
        }}
        onClick={handleProgressClick}
      >
        <Box
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: 'var(--mantine-color-gray-6)',
            borderRadius: '3px',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            top: '-3.5px',
            left: `${progress}%`,
            width: '12px',
            height: '12px',
            backgroundColor: 'var(--mantine-color-gray-6)',
            borderRadius: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        />
      </Box>

      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSecondsToTime(duration)}
      </Text>

      {fileName && (
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.6" truncate style={{ maxWidth: '200px' }}>
          {fileName}
        </Text>
      )}

      <Button
        variant="subtle"
        size="sm"
        style={{
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          padding: 0,
          backgroundColor: 'var(--mantine-color-gray-2)',
          marginLeft: '16px',
        }}
        onClick={onMuteToggle}
      >
        {isMuted ? (
          <IconVolumeOff size={18} color="black" />
        ) : (
          <IconVolume size={18} color="black" />
        )}
      </Button>

      {onClose && (
        <ActionIcon variant="subtle" size="sm" onClick={onClose} aria-label="Close player">
          <IconX size={16} color="black" />
        </ActionIcon>
      )}
    </Box>
  );
};
